import { type ReactNode, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Extra content rendered inside the header, below the title/close row
   * (e.g. a stepper). When provided the header stacks vertically.
   */
  headerExtra?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className = '',
  headerExtra,
}: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal modal--${size}${className ? ` ${className}` : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={`modal__head${headerExtra ? ' modal__head--stacked' : ''}`}>
          {headerExtra ? (
            <>
              <div className="modal__head-row">
                {title && <h2 className="modal__title">{title}</h2>}
                <button className="btn-icon btn-icon--labeled" onClick={onClose} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                  <span>Close</span>
                </button>
              </div>
              {headerExtra}
            </>
          ) : (
            <>
              {title && <h2 className="modal__title">{title}</h2>}
              <button className="btn-icon btn-icon--labeled" onClick={onClose} aria-label="Close">
                <span aria-hidden="true">&times;</span>
                <span>Close</span>
              </button>
            </>
          )}
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
